// Searing Truth pure helpers. No React, no Three.js.
//
// ── Codex (Searing Truth, Magic) ──
//   "Target player draws and reveals two spells, then takes damage
//   equal to the higher mana cost."
//
// This is the one card in the current backlog that genuinely needs a
// cross-client handshake. In multiplayer each client owns its own hand
// (addToHand always routes to the LOCAL handCards in non-sandbox play),
// so a caster CANNOT draw spells into the opponent's hand from its own
// client. The cards would land in the caster's hand instead, and the
// per-turn draw counters / on_draw watchers would fire for the wrong
// seat. The draw therefore has to run on the TARGET player's own client.
//
// Flow (caster C, target T):
//   1. At cast C is shown a target-player pick (Opponent / Yourself); the
//      chosen seat becomes T. C emits a `forceRevealDraw` request carrying
//      { targetSeat, count, requestId }.
//   2. T's handler draws `count` from its OWN spellbook into its OWN
//      hand through the unified draw funnel (pile:sync + hand:info +
//      on_draw all fire for the right seat), marks each drawn card
//      publicly revealed, computes the higher printed mana cost, and
//      replies `revealDrawResult` with { requestId, higherCost }.
//   3. C's handler deals `higherCost` to the target player's Avatar via
//      the damage funnel.
//
// The handshake mirrors the request/result shape of card:animate /
// card:revert: a one-way request from the actor, a one-way reply from
// the affected client. Game actions relay only to the OTHER peer, so
// the request always lands on the opponent and the reply always lands
// back on the caster with no self-filtering needed.

import { msg } from '../../../../logMessage';

/**
 * Caster side. Resolve the target seat and emit the forced reveal-draw
 * request. The actual draw happens on the target's client.
 *
 * Host adapter:
 *   opponentSeat(controller): 'p1' | 'p2'. The other seat.
 *   newRequestId(): string
 *   emitForceRevealDraw(targetSeat, count, requestId): void
 *   logAction(key, values?): void
 *
 * @param {object} sourceCard  the Searing Truth instance
 * @param {string} controller  'p1' | 'p2'. The caster's seat
 * @param {object} args        { count?: number }. Defaults to 2
 * @param {object} host
 * @returns {string} the request id (so callers can correlate the reply)
 */
export function castForceRevealDraw(sourceCard, controller, args, host) {
  const count = args?.count ?? 2;
  const targetSeat = host.opponentSeat(controller);
  const requestId = host.newRequestId();
  host.logAction('{source}: {who} draws and reveals {n} spells', {
    source: sourceCard.name || msg('Searing Truth'),
    who: targetSeat === 'p1' ? msg('Player 1') : msg('Player 2'),
    n: count,
  });
  host.emitForceRevealDraw(targetSeat, count, requestId);
  return requestId;
}

/**
 * Target side. Draw `count` spells into this client's own hand, reveal
 * them, and compute the higher printed mana cost. Pure orchestration.
 * every side effect routes through the host adapter.
 *
 * Host adapter:
 *   drawRevealedSpell(): object | null. Draw one spell from THIS
 *     client's own spellbook into hand, mark it revealed, return the
 *     drawn instance (or null when the spellbook is empty).
 *   broadcastHand(): void (optional). Legacy hand-info push; the
 *     authoritative diff carries reveals now, so hosts may omit it.
 *   costOf(cardInstance): number. Printed mana cost.
 *
 * @param {number} count
 * @param {object} host
 * @returns {number} the higher printed mana cost of the drawn cards (0
 *   when nothing was drawn)
 */
export function resolveForceRevealDraw(count, host) {
  const drawn = [];
  for (let i = 0; i < count; i++) {
    const card = host.drawRevealedSpell();
    if (!card) break;
    drawn.push(card);
  }
  if (drawn.length > 0) host.broadcastHand?.();
  let higher = 0;
  for (const card of drawn) {
    const cost = host.costOf(card) || 0;
    if (cost > higher) higher = cost;
  }
  return higher;
}
